import { watch } from 'vue'

interface SEOOptions {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
  author?: string
}

export function useSEO() {
  const updateMetaTags = (options: SEOOptions) => {
    const {
      title,
      description,
      image,
      url,
      type = 'website',
      publishedTime,
      modifiedTime,
      author
    } = options

    // Actualizar título de la página
    if (title) {
      document.title = `${title} | Fourin Blog`
    }

    // Actualizar o crear meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    if (description) {
      metaDescription.setAttribute('content', description)
    }

    // Actualizar o crear Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'og:type', content: type }
    ]

    ogTags.forEach(({ property, content }) => {
      if (content) {
        let meta = document.querySelector(`meta[property="${property}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('property', property)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      }
    })

    // Actualizar o crear Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image }
    ]

    twitterTags.forEach(({ name, content }) => {
      if (content) {
        let meta = document.querySelector(`meta[name="${name}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('name', name)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      }
    })

    // Actualizar datos estructurados (Schema.org)
    if (type === 'article' && title) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]')
      if (!schemaScript) {
        schemaScript = document.createElement('script')
        schemaScript.setAttribute('type', 'application/ld+json')
        document.head.appendChild(schemaScript)
      }

      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url
        },
        headline: title,
        image: image,
        author: {
          '@type': 'Person',
          name: author
        },
        publisher: {
          '@type': 'Organization',
          name: 'Fourin',
          logo: {
            '@type': 'ImageObject',
            url: 'https://fourin.co/logo.png'
          }
        },
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime
      }

      schemaScript.textContent = JSON.stringify(schema)
    }
  }

  const resetMetaTags = () => {
    // Resetear a valores por defecto
    document.title = 'Fourin - Soluciones de Gestión de Activos'

    const defaultDescription = 'Fourin ofrece soluciones integrales de gestión de activos, mantenimiento predictivo y eficiencia operativa para empresas.'

    updateMetaTags({
      title: 'Fourin - Soluciones de Gestión de Activos',
      description: defaultDescription,
      url: 'https://fourin.co',
      type: 'website'
    })
  }

  return {
    updateMetaTags,
    resetMetaTags
  }
}
